import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
} from '@mui/material';
import { Proposta } from '../../types/proposta';
import { formatarCPFCNPJ, formatarData, formatarPhone, formatarValor } from '../../utils/formatToView';

interface PropostaViewDialogProps {
  open: boolean;
  onClose: () => void;
  proposta: Proposta | null;
  onExportPdf?: () => void;
}

function montarEndereco(p: Proposta): string {
  const partes: string[] = [];
  if (p.endereco) {
    let linha = p.endereco;
    if (p.numero) linha += `, ${p.numero}`;
    partes.push(linha);
  }
  if (p.complemento) partes.push(p.complemento);
  if (p.bairro) partes.push(p.bairro);
  if (p.cidade || p.estado) partes.push([p.cidade, p.estado].filter(Boolean).join(' - '));
  return partes.join(' | ') || '-';
}

const InfoItem: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2">{value || '-'}</Typography>
  </Grid>
);

const PropostaViewDialog: React.FC<PropostaViewDialogProps> = ({ open, onClose, proposta, onExportPdf }) => {
  if (!proposta) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Proposta #{proposta.id} — {proposta.nome_cliente}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" color="primary" gutterBottom>Cliente</Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <InfoItem label="Nome" value={proposta.nome_cliente} />
          <InfoItem label="CPF / CNPJ" value={proposta.cpf_cnpj ? formatarCPFCNPJ(proposta.cpf_cnpj) : '-'} />
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">Endereço</Typography>
            <Typography variant="body2">{montarEndereco(proposta)}</Typography>
          </Grid>
          <InfoItem label="Contato" value={proposta.nome_contato} />
          <InfoItem label="Telefone" value={
            proposta.fone_contato ? formatarPhone(proposta.fone_contato, '55') : '-'
          } />
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="primary" gutterBottom>Proposta</Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <InfoItem label="Data da Proposta" value={formatarData(proposta.data_proposta)} />
          <InfoItem label="Vencimento" value={formatarData(proposta.data_vencimento)} />
          <InfoItem label="Criado por" value={proposta.usuario?.nome} />
          <InfoItem label="Valor Total" value={formatarValor(Number(proposta.valor_total))} />
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="primary" gutterBottom>Itens</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="center">Qtde</TableCell>
              <TableCell align="right">Valor Unit.</TableCell>
              <TableCell align="right">Valor Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(proposta.itens || []).map((item) => (
              <TableRow key={item.ordem}>
                <TableCell>{item.ordem}</TableCell>
                <TableCell>{item.descricao}</TableCell>
                <TableCell align="center">{item.quantidade}</TableCell>
                <TableCell align="right">{formatarValor(Number(item.valor_unitario))}</TableCell>
                <TableCell align="right">{formatarValor(Number(item.valor_total))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {proposta.observacoes && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="primary" gutterBottom>Observações</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{proposta.observacoes}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {onExportPdf && (
          <Button onClick={onExportPdf} color="secondary">Exportar PDF</Button>
        )}
        <Button onClick={onClose} variant="contained">Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropostaViewDialog;
